import { useState, useEffect } from "react";

const OTPModal = ({ onClose, onVerify, action, email, isLoading }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(300);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsExpired(true);
    }
  }, [timer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerify = () => {
    const otpCode = otp.join("");
    if (otpCode.length === 6) {
      onVerify(otpCode);
    }
  };

  const handleResend = () => {
    setTimer(300);
    setIsExpired(false);
    setOtp(["", "", "", "", "", ""]);
    document.getElementById("otp-input-0")?.focus();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-card-bg rounded-2xl border border-border-color w-[450px] p-8 shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          {action === "login" ? "Two-Factor Authentication" : "Verify Your Identity"}
        </h2>
        <p className="text-gray-400 text-sm text-center mb-2">
          {action === "login" 
            ? "Enter the 6-digit verification code sent to your email."
            : `Enter the 6-digit OTP sent to ${email || "your email"} to reset your password.`}
        </p>
        
        <p className="text-primary-cyan text-xs text-center mb-6">
          Code expires in {formatTime(timer)}
        </p>

        <div className="flex justify-center gap-3 mb-8">
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-input-${index}`}
              type="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-14 h-14 bg-bg-main border border-border-color rounded-xl text-primary-dark text-2xl font-bold text-center focus:border-primary-cyan focus:outline-none focus:ring-1 focus:ring-primary-cyan transition-all duration-200"
              autoFocus={index === 0}
              disabled={isLoading}
            />
          ))}
        </div>

        <div className="text-center mb-6">
          {!isExpired ? (
            <p className="text-primary-cyan text-sm">
              Code expires in {formatTime(timer)}
            </p>
          ) : (
            <p className="text-red-400 text-sm">Code expired. Please request a new one.</p>
          )}
        </div>

        <button
          onClick={handleVerify}
          disabled={isLoading || isExpired}
          className="w-full bg-primary-cyan text-primary-dark font-semibold py-3 rounded-xl mb-4 hover:opacity-90 hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Verifying..." : "Verify Code"}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>

        <div className="text-center">
          {isExpired ? (
            <button
              onClick={handleResend}
              className="text-primary-cyan text-sm hover:underline transition-all"
            >
              Didn't receive a code? Try again
            </button>
          ) : (
            <p className="text-gray-500 text-sm">
              Didn't receive a code?{" "}
              <button onClick={handleResend} className="text-primary-cyan hover:underline">
                Resend
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OTPModal;